import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { COMPANY } from "@/lib/config";
import { useLang } from "@/i18n/LanguageContext";
import { Phone, TrendingUp, Layers, Smartphone, Monitor, ArrowRight } from "lucide-react";

// Prime49 payout constant (from getprime49.com/calculator: payout = volume * B)
const PRIME49_B = 0.0042;
const TERMINAL_ICONS = [Smartphone, Monitor, Layers];
const VIEWPORT_ONCE = { once: true };

const fmtUSD = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtUSD2 = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Prime49({ fadeUp }) {
  const { d } = useLang();
  const p = d.prime49;
  const [raw, setRaw] = useState("50000");

  const volume = parseFloat(raw.replace(/[^0-9.]/g, "")) || 0;
  const monthly = volume * PRIME49_B;
  const annual = monthly * 12;

  const onChange = (e) => setRaw(e.target.value.replace(/[^0-9]/g, ""));
  const display = raw ? Number(raw).toLocaleString("en-US") : "";

  return (
    <section id="prime49" className="relative overflow-hidden bg-secondary py-20 lg:py-28">
      <div className="pointer-events-none absolute -right-40 top-10 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 md:px-12 lg:grid-cols-2">
        {/* Left: copy */}
        <motion.div initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp}>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
            <TrendingUp className="h-4 w-4" /> {p.badge}
          </span>
          <h2 className="mt-6 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            {p.titleBefore}<span className="text-primary">{p.titleHighlight}</span>{p.titleAfter}
          </h2>
          <p className="mt-4 max-w-md text-lg text-muted-foreground">{p.desc}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {p.terminals.map((label, i) => {
              const Icon = TERMINAL_ICONS[i];
              return (
                <span key={label} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium">
                  <Icon className="h-4 w-4 text-accent" /> {label}
                </span>
              );
            })}
          </div>
          <a href={COMPANY.phoneHref} data-testid="prime49-cta-call">
            <Button className="mt-8 h-12 rounded-full bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-[hsl(326,100%,42%)] hover:text-white">
              <Phone className="mr-2 h-4 w-4" /> {p.cta}
            </Button>
          </a>
        </motion.div>

        {/* Right: calculator */}
        <motion.div
          initial="hidden" whileInView="show" viewport={VIEWPORT_ONCE} variants={fadeUp} custom={1}
          data-testid="prime49-calculator"
          className="rounded-3xl border border-border bg-white p-8 shadow-2xl sm:p-10"
        >
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">{p.calcTag}</p>
          <h3 className="mt-1 font-display text-2xl font-semibold">{p.calcHeading}</h3>
          <div className="mt-6">
            <label htmlFor="prime49-volume" className="mb-1.5 block text-sm font-medium">{p.volLabel}</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground">$</span>
              <Input
                id="prime49-volume"
                data-testid="prime49-volume-input"
                inputMode="numeric"
                value={display}
                onChange={onChange}
                placeholder="50,000"
                className="h-14 pl-8 text-lg font-semibold"
              />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="rounded-2xl bg-primary p-5 text-primary-foreground" data-testid="prime49-monthly">
              <p className="text-xs font-medium uppercase tracking-wide text-primary-foreground/80">{p.monthly}</p>
              <p className="mt-1 font-display text-3xl font-semibold">{fmtUSD2(monthly)}</p>
            </div>
            <div className="rounded-2xl bg-accent/15 p-5" data-testid="prime49-annual">
              <p className="text-xs font-medium uppercase tracking-wide text-accent">{p.yearly}</p>
              <p className="mt-1 font-display text-3xl font-semibold text-foreground">{fmtUSD(annual)}</p>
            </div>
          </div>

          <a href="#quote" data-testid="prime49-cta-quote">
            <Button className="mt-6 h-12 w-full rounded-full bg-foreground text-base font-semibold text-background hover:bg-foreground/90">
              {p.calcCta} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <p className="mt-3 text-center text-xs text-muted-foreground">{p.disclaimer}</p>
        </motion.div>
      </div>
    </section>
  );
}
